// Stream the two call legs to the server for live transcription.
//
// The agent's microphone and the customer's audio are ALREADY separate objects
// in the peer connection — senders and receivers. Sending them as two streams
// means the speaker is decided by which stream the audio came from, not by a
// model listening to a mixed recording and guessing. The post-call path has to
// diarize because the recording is mixed by then; here we simply do not mix.
//
// RAW PCM, NOT MediaRecorder. MediaRecorder emits WebM/Opus, and every chunk
// after the first is a fragment that only makes sense following the container
// header — a realtime endpoint reading chunk 40 in isolation gets nothing. So
// this taps the audio graph and ships linear16 at 16kHz, which is what the
// server tells Deepgram to expect and what telephony audio is anyway.
//
// NOTHING HERE MAY AFFECT THE CALL. Every entry point is wrapped, the nodes are
// read-only taps that never join the outbound path, and a failure returns a
// no-op stopper. The worst outcome is no live transcript.

export interface LiveTurn {
  leg: "agent" | "customer";
  text: string;
  isFinal: boolean;
  /** Seconds into this leg's stream, from the provider. */
  startSec: number | null;
}

/** Deepgram is told 16kHz on the server; both ends have to agree. */
const TARGET_RATE = 16000;
/** ~250ms at 16kHz. Small enough to feel live, large enough not to spam. */
const FRAME = 4096;

interface Session { stop: () => void }

/**
 * Open one socket per leg and pump audio into it.
 *
 * Returns a stopper that is always safe to call, including when nothing
 * started — the caller should not have to know whether this worked.
 */
// PCI GATE, SHARED WITH THE RECORDING PAUSE. Pausing the recording mutes the
// switch's wav — and the very first live test showed the card number arriving
// anyway, in the TRANSCRIPT: this tap streams to Deepgram with no idea the
// desk just declared a payment in progress, and a transcript is storage under
// PCI DSS exactly as a recording is. So the desk sets this flag alongside the
// recording pause and the audio frames simply stop being sent — both legs,
// because the customer reads the number and the agent repeats it back.
// Module-scoped rather than threaded through useSip: one desk, one flag, and
// the reset on every startLiveTranscribe() keeps a new call clean.
let transcribePaused = false;
export function setLiveTranscribePaused(paused: boolean): void {
  transcribePaused = paused;
}

export function startLiveTranscribe(opts: {
  pc: RTCPeerConnection;
  workspace: string;
  /** The Helix call this audio belongs to. Optional: without it the agent still
   *  sees their own transcript, but the server has nothing to attribute the
   *  turns to, so live assist stays quiet. The server does NOT trust it — it is
   *  checked against the authenticated tenant before anything is published. */
  callId?: string | null;
  onTurn: (turn: LiveTurn) => void;
  onUnavailable?: () => void;
  /** The interaction the server resolved our callId to — the id the assist bus
   *  publishes coaching under, so the desk can show the rail for THIS call and
   *  not for every conversation in the workspace. */
  onConversation?: (conversationId: string) => void;
}): Session {
  const { pc, workspace, callId, onTurn, onUnavailable, onConversation } = opts;
  // A new call must never inherit the previous call's payment pause.
  transcribePaused = false;
  const closers: (() => void)[] = [];
  const stop = () => { for (const c of closers) { try { c(); } catch { /* ignore */ } } closers.length = 0; };

  let ctx: AudioContext | null = null;
  try {
    const AudioCtor: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return { stop };
    ctx = new AudioCtor();
  } catch {
    return { stop };
  }
  const audio = ctx;
  closers.push(() => void audio.close().catch(() => { /* already gone */ }));

  const openLeg = (leg: "agent" | "customer", track: MediaStreamTrack) => {
    let ws: WebSocket;
    try {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(
        `${proto}//${window.location.host}/ops/api/voice/softphone/live-ws` +
        `?workspace=${encodeURIComponent(workspace)}&leg=${leg}` +
        (callId ? `&callId=${encodeURIComponent(callId)}` : ""),
      );
      ws.binaryType = "arraybuffer";
    } catch {
      return;
    }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(String(e.data)) as
          | { type: "transcript"; leg: "agent" | "customer"; text: string; isFinal: boolean; startSec: number | null }
          | { type: "ready"; conversationId?: string | null }
          | { type: "unavailable"; reason?: string };
        if (msg.type === "transcript") onTurn(msg);
        else if (msg.type === "unavailable") onUnavailable?.();
        else if (msg.type === "ready" && msg.conversationId) onConversation?.(msg.conversationId);
      } catch { /* not ours */ }
    };
    ws.onerror = () => { /* the call continues without a transcript */ };

    // A READ-ONLY TAP. The source feeds the processor and the processor's output
    // goes nowhere audible — connecting it to the destination would echo the
    // customer back into the room, and worse, feed the agent's own mic back.
    // A zero-gain node keeps the graph alive without producing sound.
    const source = audio.createMediaStreamSource(new MediaStream([track]));
    const proc = audio.createScriptProcessor(FRAME, 1, 1);
    const mute = audio.createGain();
    mute.gain.value = 0;

    const ratio = audio.sampleRate / TARGET_RATE;
    proc.onaudioprocess = (ev) => {
      // Payment in progress: the frames are dropped HERE, before they leave the
      // browser — not filtered later. What is never sent cannot be stored.
      if (transcribePaused) return;
      if (ws.readyState !== WebSocket.OPEN) return;
      const input = ev.inputBuffer.getChannelData(0);
      // Decimate to 16kHz and convert float [-1,1] to signed 16-bit. Nearest
      // sample rather than a resampling filter: telephony audio is band-limited
      // to ~3.4kHz, so there is nothing above 8kHz for aliasing to fold down.
      const outLen = Math.floor(input.length / ratio);
      const pcm = new Int16Array(outLen);
      for (let i = 0; i < outLen; i++) {
        const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      try { ws.send(pcm.buffer); } catch { /* socket went away */ }
    };

    source.connect(proc);
    proc.connect(mute);
    mute.connect(audio.destination);

    closers.push(() => {
      try { proc.onaudioprocess = null; } catch { /* ignore */ }
      try { source.disconnect(); proc.disconnect(); mute.disconnect(); } catch { /* ignore */ }
      try { ws.close(); } catch { /* ignore */ }
    });
  };

  try {
    // Receivers are the far end; senders are this agent. Same split the mixed
    // recorder uses, kept apart instead of summed.
    const remote = pc.getReceivers().map((r) => r.track).find((t) => t && t.kind === "audio");
    const local = pc.getSenders().map((s) => s.track).find((t) => t && t.kind === "audio");
    if (remote) openLeg("customer", remote);
    if (local) openLeg("agent", local);
  } catch {
    stop();
  }

  return { stop };
}
